import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import { LocalFind, LocalInsert } from '@weblite/persist'
import { retryPromiseUntilSucceed } from '@weblite/logic-toolkit'
import {
  DEVICE_MODEL_FROM_NAVIGATOR,
  getCurrentRelease,
} from '$logic/client-device'
import {
  Profile,
  getProfile,
  convertProfileToAnalyticsFormat,
} from '$logic/profile'
import { envMode, envModes } from '$config/env'
import { Client, Event } from '$logic/analytics/types'
import { bufferFunction } from '$func/buffer-function'
import { DeferredPromise } from '$func/deferred-promise'
import { getClientUserIdentityToken, isLoggedIn } from '$logic/auth'
import { DEVICE_MODEL, DEVICE_VENDOR, twaVersion } from '$func/environmentInfo'
import * as analyticsSync from './offline-sync'
import { postRequest } from './api'

const CREATE_SESSION_RETRY_INTERVALS = [2000, 3000, 4000, 5000]
const getExponentialDelayForCreatingSession = (retryCount: number) =>
  CREATE_SESSION_RETRY_INTERVALS[
    Math.min(retryCount - 1, CREATE_SESSION_RETRY_INTERVALS.length - 1)
  ]

let identifiedSessionIdPromise: DeferredPromise<void> | undefined
let anonymousSessionIdPromise: DeferredPromise<void> | undefined

const ANALYTICS_BUFFER_TIME = 3000
const DEVICE_ID = 'deviceId'
export const deviceId = LocalFind(DEVICE_ID) || LocalInsert(DEVICE_ID, uuidv4())

const SESSION_NUMBER = 'sessionNumber'
const sessionNumber = LocalInsert(
  SESSION_NUMBER,
  (LocalFind(SESSION_NUMBER) || 0) + 1,
)

const SESSION_START_TIME = 'sessionStartTime'
const sessionStartTime = LocalInsert(SESSION_START_TIME, new Date())

let identifiedSessionId = ''
let anonymousSessionId = ''

export async function createAnonymousSession() {
  if (anonymousSessionId) return
  const { version } = getCurrentRelease()
  const client: Client = {
    dId: deviceId,
    sNum: sessionNumber,
    sTime: sessionStartTime,
    appVer: version,
    ...(twaVersion && { twaVersion }),
    device: {
      model: DEVICE_MODEL_FROM_NAVIGATOR || DEVICE_MODEL,
      vendor: DEVICE_VENDOR,
    },
  }
  const res = await postRequest<string>('', {
    user: {},
    client,
  })
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  anonymousSessionId = res?.data.sessionId
}

export async function createIdentifiedSession() {
  if (identifiedSessionId) return
  const userJWT = await getClientUserIdentityToken()
  const rawProfile = getProfile() as Profile

  const profile = convertProfileToAnalyticsFormat(rawProfile)

  const { version } = getCurrentRelease()
  const client: Client = {
    dId: deviceId,
    sNum: sessionNumber,
    sTime: sessionStartTime,
    appVer: version,
    ...(twaVersion && { twaVersion }),
    device: {
      model: DEVICE_MODEL_FROM_NAVIGATOR || DEVICE_MODEL,
      vendor: DEVICE_VENDOR,
    },
  }

  const res = await postRequest<string>(
    ``,
    {
      user: profile,
      client,
    },
    { headers: { authorization: `Bearer ${userJWT}` } },
  )
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line prefer-destructuring
  identifiedSessionId = res?.data.sessionId
}

function isThereAnyOpenedSession() {
  const isUserLoggedIn = isLoggedIn()

  return (
    (!isUserLoggedIn && anonymousSessionId) ||
    (isUserLoggedIn && identifiedSessionId)
  )
}

async function createSession() {
  const isUserLoggedIn = isLoggedIn()

  if (isUserLoggedIn && !identifiedSessionId) {
    if (!identifiedSessionIdPromise) {
      identifiedSessionIdPromise = new DeferredPromise()
      await retryPromiseUntilSucceed(
        createIdentifiedSession,
        getExponentialDelayForCreatingSession,
      )()
      identifiedSessionIdPromise.resolve()
      identifiedSessionIdPromise = undefined
    } else {
      await identifiedSessionIdPromise.wait()
    }
  }
  if (!isUserLoggedIn && !anonymousSessionId) {
    if (!anonymousSessionIdPromise) {
      anonymousSessionIdPromise = new DeferredPromise()
      await retryPromiseUntilSucceed(
        createAnonymousSession,
        getExponentialDelayForCreatingSession,
      )()
      anonymousSessionIdPromise.resolve()
      anonymousSessionIdPromise = undefined
    } else {
      await anonymousSessionIdPromise.wait()
    }
  }
}

export const sendAnalyticsRequest = async (events: Event[]) => {
  const isUserLoggedIn = isLoggedIn()
  if (!isThereAnyOpenedSession()) await createSession()

  try {
    await postRequest(
      `${isUserLoggedIn ? identifiedSessionId : anonymousSessionId}/events`,
      {
        events,
      },
    )
  } catch (e: any) {
    if (e?.response?.data?.message === 'SESSION_NOT_FOUND' && isUserLoggedIn) {
      identifiedSessionId = ''
      await createSession()
      await sendAnalyticsRequest(events)
    } else {
      throw e
    }
  }
}

const sendAnalyticsRequestWithPersist = async (events: Event[]) => {
  const unset = await analyticsSync.set(uuidv4(), { events })

  retryPromiseUntilSucceed(sendAnalyticsRequest, () => 1000)(events).then(unset)
}

export const sendPersistedAnalytics = async () => {
  const { items, del, release } = await analyticsSync.getAll()

  const events = R.flatten(
    Object.values(items).map(({ patch: { events } }) => events),
  )

  release()

  if (events.length > 0)
    retryPromiseUntilSucceed(sendAnalyticsRequest, () => 1000)(events).then(del)
}

export const sendAnalytics = bufferFunction(async (events: Event[]) => {
  if (envMode !== envModes.PRODUCTION) return

  const { persistent, nonPersistent } = R.groupBy(
    item => (item.persist ? 'persistent' : 'nonPersistent'),
    events,
  )

  if (nonPersistent?.length) void sendAnalyticsRequest(nonPersistent)
  if (persistent?.length) void sendAnalyticsRequestWithPersist(persistent)
}, ANALYTICS_BUFFER_TIME)

export const sendAnalyticsImmediately = async (event: Event) => {
  if (envMode !== envModes.PRODUCTION) return

  if (!event.persist) void sendAnalyticsRequest([event])
  if (event.persist) void sendAnalyticsRequestWithPersist([event])
}

export async function migrateFromAnonymousSessionToIdentifiedSession() {
  const userJWT = await getClientUserIdentityToken()
  if (!anonymousSessionId) return
  await postRequest<string>(`${anonymousSessionId}/login`, null, {
    headers: {
      authorization: `Bearer ${userJWT}`,
    },
  })
  anonymousSessionId = ''
}
